import React, { useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { useAppDispatch } from 'Libs/redux/store';
import { mapLanguageIdsToLanguageCodes } from 'Pages/Activity/activity.mapper';
import { CampaignArgs, createCampaign } from 'Store/entities/activity/activity.thunks';
import { redirect } from 'Libs/routing';

import { CampaignMode, CampaignForm } from '../CampaignForm/CampaignForm';

export type UseCampaignForm = (args: { onGoBack: () => void }) => {
  form: JSX.Element;
  canSubmit: boolean;
  submit: () => void;
  reset: () => void;
};

export const useCampaignForm: UseCampaignForm = ({ onGoBack }) => {
  const submitting = useRef(false);
  const dispatch = useAppDispatch();

  const { watch, setValue, reset, getValues } = useForm({
    defaultValues: {
      name: '',
      selectedCampaignMode: 'manual',
      selectedLanguageId: 0,
    },
  });

  const submit = useCallback(async () => {
    if (submitting.current) return;

    const { name, selectedCampaignMode, selectedLanguageId } = getValues();
    const languageCode = mapLanguageIdsToLanguageCodes([selectedLanguageId])[0];

    const campaign: CampaignArgs = {
      informations: {
        name: {
          [languageCode]: name,
        },
      },
      languages: [languageCode],
      scheduling: {
        mode: selectedCampaignMode as CampaignMode,
        type: 'flexible',
      },
      status: 'draft',
    };

    submitting.current = true;

    const { unwrap } = dispatch(createCampaign(campaign));

    try {
      const result = await unwrap();

      redirect(`/activity/campaign/${result.id}`);
    } catch (error) {
      console.error(error);
    } finally {
      submitting.current = false;
    }
  }, [dispatch, getValues]);

  const canSubmit = !!watch('name') && !!watch('selectedLanguageId');

  return {
    reset,
    submit,
    canSubmit,
    form: (
      <CampaignForm
        name={watch('name')}
        onNameChange={(name) => setValue('name', name)}
        onCampaignModeSelect={(mode) => setValue('selectedCampaignMode', mode)}
        onLanguageSelect={(language) => setValue('selectedLanguageId', language.id)}
        selectedCampaignMode={watch('selectedCampaignMode') as CampaignMode}
        selectedLanguageId={watch('selectedLanguageId')}
        onBack={onGoBack}
      />
    ),
  };
};
